import { REFRESH_ACCESS_TOKEN_ERROR } from './token-refresh';
import type { AuthRejectionReason } from './session-rejected-signal';

/**
 * Erkennung des Zustands „angemeldet, aber die API weist uns ab".
 *
 * Hintergrund: Scheitert der Token-Refresh endgültig, bleibt die next-auth-
 * Session bestehen (Name und Kundennummer stehen weiter in der Sidebar),
 * während jede GraphQL-Anfrage ohne gültiges Bearer-Token rausgeht und mit
 * „Unauthorized" zurückkommt. Sichtbar war davon bisher nur „Fehler beim
 * Laden …" — ohne Hinweis, dass eine Neuanmeldung hilft.
 *
 * Bewusst frei von React- und next-auth-Importen, damit die Regel testbar
 * bleibt (gleiches Vorgehen wie `portal-access.ts`).
 */

/** Der für diese Entscheidung nötige Teil der next-auth-Session. */
export interface RenewableSession {
  accessToken?: string;
  error?: string;
  user?: { customerNumber?: string | null };
}

/**
 * Meldungen des API-Guards, die auf eine nicht mehr angenommene Anmeldung
 * hindeuten. Beide stammen aus
 * libs/api/crm-documents/feature-auth/.../auth.guard.ts bzw. aus
 * nest-keycloak-connect.
 */
const REJECTION_MESSAGES: Record<string, AuthRejectionReason> = {
  // Wirft nest-keycloak-connect ohne Text — sowohl bei fehlendem Header als
  // auch bei einem Token, den Keycloak nicht mehr für aktiv hält.
  Unauthorized: 'unauthorized',
  // Der Token trägt die Kundennummer nicht (mehr) — etwa direkt nach einer
  // Änderung des Keycloak-Attributs. Eine Neuanmeldung holt sie in den Token.
  'JWT token missing customerNumber claim': 'missing-claim',
};

/**
 * Deutet die Fehlermeldungen einer GraphQL-Antwort.
 *
 * `hadAuthHeader` ist die entscheidende Zusatzbedingung: ohne mitgeschicktes
 * Token ist eine Ablehnung kein Hinweis auf eine tote Session — diesen Fall
 * erkennt sessionNeedsRenewal schon am fehlenden accessToken.
 */
export function classifyAuthRejection(
  messages: string[],
  hadAuthHeader: boolean,
): AuthRejectionReason | null {
  if (!hadAuthHeader) return null;
  for (const message of messages) {
    const reason = REJECTION_MESSAGES[message];
    if (reason) return reason;
  }
  return null;
}

/** Status von `useSession()`. */
export type SessionStatus = 'loading' | 'authenticated' | 'unauthenticated';

export function sessionNeedsRenewal(
  session: RenewableSession | null | undefined,
  status: SessionStatus,
  rejection: AuthRejectionReason | null = null,
): boolean {
  if (status !== 'authenticated') return false;
  if (session?.error === REFRESH_ACCESS_TOKEN_ERROR) return true;
  // Die API hat unseren Token abgelehnt, obwohl er lokal gültig aussieht.
  if (rejection === 'unauthorized') return true;
  // Fehlender Claim hilft nur weiter, wenn die Session die Kundennummer
  // kennt: dann ist bloß der Token veraltet. Einem Registrierungs-Nutzer
  // ohne Kundennummer bringt eine Neuanmeldung nichts.
  if (rejection === 'missing-claim' && session?.user?.customerNumber) {
    return true;
  }
  // Ein angemeldeter Nutzer hat immer ein Access-Token — auch der noch nicht
  // freigeschaltete Registrierungs-Nutzer. Fehlt es, ist der Refresh
  // gescheitert. Fängt zusätzlich die Sessions, deren Cookie noch aus der
  // Zeit vor dem Fehlersignal stammt.
  return !session?.accessToken;
}
